import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal, { Eyebrow } from "../../components/Reveal";
import { SERVICES, SECONDARY_SERVICES } from "../../constants/content";
import { useSite } from "../../context/SiteContext";

const SERVICE_IMG_KEYS = { renovations: "renovation", extensions: "deck", "new-builds": "newbuild" };

export default function Services() {
  const { images } = useSite();
  return (
    <section id="services" data-testid="services-section" className="py-16 md:py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="space-y-16 md:space-y-24">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={0.05}>
              <article data-testid={`service-block-${s.slug}`} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center`}>
                <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="relative overflow-hidden group">
                    <img src={images[SERVICE_IMG_KEYS[s.slug]] || s.image} alt={s.imageAlt} loading="lazy" className="w-full aspect-[4/3] object-cover img-duotone transition-transform duration-700 group-hover:scale-[1.03]" />
                    <span className="absolute bottom-0 left-0 bg-ink text-gold font-mono text-sm px-4 py-3 tracking-[0.2em]">{s.num}</span>
                  </div>
                </div>
                <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""} lg:px-6`}>
                  <h3 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-ink uppercase display-expanded">{s.title}</h3>
                  <p className="mt-3 font-display text-lg md:text-xl text-golddark font-medium">{s.tagline}</p>
                  <p className="mt-4 text-slate2 leading-relaxed max-w-lg">{s.desc}</p>
                  <div className="mt-7 flex flex-wrap gap-3">
                    <Link to={`/contact?type=${encodeURIComponent(s.enquiryType)}`} data-testid={`service-cta-${s.slug}`} className="inline-flex items-center gap-2.5 bg-ink hover:bg-charcoal text-white font-mono text-[11px] tracking-[0.18em] uppercase px-6 py-3.5 transition-colors">
                      {s.cta} <ArrowRight className="w-4 h-4 text-gold" />
                    </Link>
                    <Link to={`/${s.slug}`} data-testid={`service-more-${s.slug}`} className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase text-slate2 hover:text-ink px-2 py-3.5 transition-colors">
                      Learn More
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.05}>
          <div className="mt-20 md:mt-28 pt-12 md:pt-16 border-t border-line">
            <Eyebrow>We Also Undertake</Eyebrow>
            <p className="mt-4 text-slate2 max-w-2xl text-sm md:text-base leading-relaxed">
              You don't need a major project to get in touch — we regularly take on smaller residential building work across the region.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
              {SECONDARY_SERVICES.map((s) => (
                <Link key={s.title} to={s.link || `/contact?type=${encodeURIComponent(s.type)}`} data-testid={`secondary-service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`group bg-white p-6 hover:bg-paper transition-colors ${s.urgent ? "border-l-2 border-l-[#B3261E]" : ""}`}>
                  <h4 className={`font-display font-semibold text-base leading-snug transition-colors ${s.urgent ? "text-[#B3261E]" : "text-ink group-hover:text-golddark"}`}>
                    {s.urgent && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#B3261E] mr-2 align-middle" />}
                    {s.title}
                  </h4>
                  <p className="mt-2.5 text-xs text-slate2 leading-relaxed">{s.desc}</p>
                </Link>
              ))}
            </div>
            <p className="mt-8 text-sm text-slate2 leading-relaxed max-w-2xl">
              Not sure which category your job fits? It doesn't matter — tell us what you need and we'll point you in the right direction.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
